import { format, isValid, parse } from "date-fns";
import { formatInTenantZone, resolveLocale, tenantDateFnsLocale, tenantLocale } from "@/helpers/tenantCalendar";

/*
 * Every date the UI shows uses one of these named formats: a call site picks a format by what it
 * shows ("a date and time"), and the pattern for each tenant locale lives here once. The locales
 * are the backend's TenantCalendar.SupportedLocales -- extend both together.
 *
 * Month and weekday names come from the tenant's date-fns locale. File-name stamps are not display
 * formats and stay fixed (see pdfFileName and McExport).
 */
export type TenantDateFormat =
  | "date"
  | "dateMedium"
  | "dateLong"
  | "time"
  | "dateTime"
  | "dateTimeSeconds"
  | "dateTimeMedium"
  | "dateTimeLong"
  | "dayTime";

type TenantDatePatterns = Record<TenantDateFormat, string>;

const UK_PATTERNS: TenantDatePatterns = {
  date: "dd/MM/yyyy", //                     07/10/2026
  dateMedium: "d MMM yyyy", //               7 Oct 2026
  dateLong: "EEE, d MMM yyyy", //            Wed, 7 Oct 2026
  time: "HH:mm", //                          14:05
  dateTime: "dd/MM/yyyy HH:mm", //           07/10/2026 14:05
  dateTimeSeconds: "dd/MM/yyyy HH:mm:ss", // 07/10/2026 14:05:09
  dateTimeMedium: "d MMM yyyy HH:mm", //     7 Oct 2026 14:05
  dateTimeLong: "EEE, d MMM yyyy, HH:mm", // Wed, 7 Oct 2026, 14:05
  dayTime: "EEE, d MMM 'at' hh:mma", //      Wed, 7 Oct at 02:05PM
};

const US_PATTERNS: TenantDatePatterns = {
  date: "MM/dd/yyyy", //                       10/07/2026
  dateMedium: "MMM d, yyyy", //                Oct 7, 2026
  dateLong: "EEE, MMM d, yyyy", //             Wed, Oct 7, 2026
  time: "h:mm a", //                           2:05 PM
  dateTime: "MM/dd/yyyy h:mm a", //            10/07/2026 2:05 PM
  dateTimeSeconds: "MM/dd/yyyy h:mm:ss a", //  10/07/2026 2:05:09 PM
  dateTimeMedium: "MMM d, yyyy h:mm a", //     Oct 7, 2026 2:05 PM
  dateTimeLong: "EEE, MMM d, yyyy, h:mm a", // Wed, Oct 7, 2026, 2:05 PM
  dayTime: "EEE, MMM d 'at' h:mma", //         Wed, Oct 7 at 2:05PM
};

// The pattern for a named format, in the tenant's locale unless one is given. Date pickers need
// the concrete pattern (they parse typed input with it), so it is exposed as well as used here.
export const tenantDatePattern = (name: TenantDateFormat, locale: string = tenantLocale()): string => {
  return isMonthFirstLocale(locale) ? US_PATTERNS[name] : UK_PATTERNS[name];
};

// Whether the locale writes the month before the day ("Oct 7") -- for labels assembled from parts,
// such as date ranges, that cannot be a single pattern.
export const isMonthFirstLocale = (locale: string = tenantLocale()): boolean => {
  return resolveLocale(locale).toLowerCase() === "en-us";
};

// An API instant (UTC), shown on the tenant's wall clock.
export const formatTenantDate = (instant: Date | string | number | null | undefined, name: TenantDateFormat): string => {
  if (instant === null || instant === undefined || instant === "") return "";
  return formatInTenantZone(instant, tenantDatePattern(name));
};

// A Date that already holds tenant wall-clock fields (filter ranges, picker values): no zone shift.
export const formatTenantWallClock = (date: Date | null | undefined, name: TenantDateFormat): string => {
  if (!date || !isValid(date)) return "";
  return format(date, tenantDatePattern(name), { locale: tenantDateFnsLocale() });
};

// The inverse of formatTenantWallClock: text produced with a named format (e.g. a grouping key),
// read back as tenant wall-clock fields with the same pattern and locale. Invalid Date if it doesn't match.
export const parseTenantWallClock = (text: string, name: TenantDateFormat): Date => {
  return parse(text, tenantDatePattern(name), new Date(), { locale: tenantDateFnsLocale() });
};
